/**
 * The invoice record's small parts — its line columns and its total rows —
 * kept beside `InvoiceDetail.tsx` so that file stays under the size Wave 15
 * splits at (ADR-0033, Consequences).
 */

import type { ListColumn } from '../../components/page'
import type { InvoiceLine } from '../../lib/api/billing'
import { Money } from './shared'

/** The lines, as the kit's `DataTable` columns. A phone keeps the description
 *  and the amount. */
export const LINE_COLUMNS: ListColumn<InvoiceLine>[] = [
  {
    key: 'description',
    label: 'Description',
    width: 220,
    phone: true,
    render: (line) => (
      <span className="block max-w-[12rem] min-w-0 break-words whitespace-normal sm:max-w-sm">
        {line.description}
        {line.source === 'expense' && (
          <span className="text-muted-foreground text-xs"> · pass-through</span>
        )}
      </span>
    ),
  },
  {
    key: 'sac_code',
    label: 'SAC',
    width: 80,
    render: (line) => <span className="text-muted-foreground">{line.sac_code ?? '—'}</span>,
  },
  {
    key: 'quantity',
    label: 'Qty',
    width: 60,
    align: 'right',
    render: (line) => <span className="tabular-nums">{line.quantity}</span>,
  },
  {
    key: 'unit_amount',
    label: 'Rate',
    width: 110,
    align: 'right',
    render: (line) => <Money value={line.unit_amount} />,
  },
  {
    key: 'gst',
    label: 'GST',
    width: 70,
    align: 'right',
    render: (line) => (
      <span className="text-muted-foreground">
        {line.gst_treatment === 'exempt' ? 'Exempt' : `${line.gst_rate}%`}
      </span>
    ),
  },
  {
    key: 'taxable_value',
    label: 'Amount',
    width: 120,
    align: 'right',
    phone: true,
    render: (line) => <Money value={line.taxable_value} />,
  },
]

/** One row of the total card. */
export function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={strong ? 'font-medium' : 'text-muted-foreground'}>{label}</dt>
      <dd className={`tabular-nums ${strong ? 'font-semibold' : ''}`}>{value}</dd>
    </div>
  )
}
